import { forwardRef } from 'react';
import { cva } from 'class-variance-authority';
import { FieldError } from 'react-hook-form';
import {
  buildCountryData,
  defaultCountries,
  parseCountry,
  PhoneInput as PhoneInputComponent,
  PhoneInputProps as PhoneInputComponentProps,
  PhoneInputRefType,
} from 'react-international-phone';
import { InputErrorSpan } from './input-error-span';
import { InputLabel } from './input-label';

//! Workaround to allow more characters for each country
// The country data is not precise enough and could cause some valid phones to not be able to be completely written.
//! TODO: Improve. Find a way to use libphone-js data in here.
const countryData = defaultCountries.map((country) => {
  const parsedCountry = parseCountry(country);
  parsedCountry.format =
    typeof parsedCountry.format === 'string' ? `${parsedCountry.format}.........` : parsedCountry.format;

  return buildCountryData(parsedCountry);
});

const inputCva = cva(
  [
    '!p-2',
    '!text-[16px]',
    '!border',
    'w-full',
    'outline-blue',
    '!rounded-md',
    '!rounded-l-none',
    '!shadow-sm',
    'focus:outline-none',
    'focus:border-sky-500',
    'focus:ring-sky-500',
    'focus:ring-1',
    'min-h-[45px]',
    'placeholder:text-slate-400',
    'placeholder:text-sm',
    'transition-colors',
    'duration-100',
    'hover:border-border-hover',
  ],
  {
    variants: {
      state: {
        default: '!border-slate-300',
        error: '!border-errorColor !outline-errorColor',
        disabled: '!bg-[#F2F2F2]',
      },
    },
  },
);

const countrySelectorCva = cva(
  [
    '!p-2',
    '!text-[16px]',
    'border',
    'w-full',
    'outline-blue',
    '!rounded-md',
    '!rounded-r-none',
    '!shadow-sm',
    'focus:outline-none',
    'focus:border-sky-500',
    'focus:ring-sky-500',
    'focus:ring-1',
    'min-h-[45px]',
    'placeholder:text-slate-400',
    'placeholder:text-sm',
    'transition-colors',
    'duration-100',
    'hover:border-border-hover',
  ],
  {
    variants: {
      state: {
        default: '!border-slate-300',
        error: '!border-errorColor !outline-errorColor',
        disabled: '!bg-[#F2F2F2]',
      },
    },
  },
);

type PhoneInputProps = Omit<PhoneInputComponentProps, 'overrideStyles' | 'inputSize' | 'onChange'> & {
  label?: React.ReactNode;
  success?: boolean;
  inputSize?: 'lg' | 'sm' | 'default';
  onChange?: (value: string) => void;
  error?: FieldError;
  defaultValue?: string;
  id?: string;
  helperText?: string;
};

export const PhoneInput = forwardRef<PhoneInputRefType, PhoneInputProps>(
  (props: PhoneInputProps, ref: React.Ref<PhoneInputRefType>) => {
    const { label, error, defaultValue, onChange, disabled, helperText, ...others } = props;
    const stateVariant = disabled ? 'disabled' : error?.message ? 'error' : 'default';
    const inputClassName = inputCva({ state: stateVariant });
    const sc = countrySelectorCva({ state: stateVariant });

    //! In order to facilitate registering this field with react-hook-form,
    const handleChange = (phone: string) => {
      if (onChange) {
        onChange(phone);
      }
    };

    return (
      <div>
        {label && <InputLabel htmlFor={others.id || ''} label={label} />}
        <PhoneInputComponent
          ref={ref}
          disableFormatting
          countries={countryData}
          onChange={handleChange}
          className="flex items-center gap-1"
          countrySelectorStyleProps={{ buttonClassName: sc }}
          inputClassName={inputClassName}
          disabled={disabled}
          value={defaultValue || ''}
          {...others}
        />
        {helperText && <div className="text-sm text-slate-400 pt-2">{helperText}</div>}
        <InputErrorSpan message={error?.message} />
      </div>
    );
  },
);
